import { fg } from '@atlaskit/platform-feature-flags';
import type { SearchExperimentLayer } from '@atlassian/search-experiment/dynamic/types';

import type { EntityATI, SearchJiraProductType } from '../../../common/types';

import { getSupportedPartials } from './get-supported-partials';
import { type ResultsSort } from './results-sort';

// eslint-disable-next-line no-barrel-files/no-barrel-files
export { getSupportedPartials } from './get-supported-partials';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { dcSpaceFilterOptionsQueryGenerator } from './dc-space-filter-options-query-generator';

export interface CommonFilters {
	range?: {
		lastModified?: {
			gt?: string;
			lt?: string;
		};
	};
	contributorsFilter?: string[];
}

export type SearchConfluenceRangeField = 'LASTMODIFIED' | 'CREATED';

export interface ConfluenceRangeFilter {
	field: SearchConfluenceRangeField;
	gt?: string;
	lt?: string;
}

export type SearchContainerStatus = 'ARCHIVED' | 'CURRENT';

export type SearchConfluenceDocumentStatus = 'CURRENT' | 'ARCHIVED' | 'DRAFT';

export interface ConfluenceFilters {
	spacesFilter: string[];
	contributorsFilter: string[];
	ancestorIdsFilter: string[];
	range: ConfluenceRangeFilter[];
	isVerified?: boolean;
	owners: string[];
	labelsFilter: string[];
	titleMatchOnly?: boolean;
	pageStatus?: string[];
	contentStatuses: SearchConfluenceDocumentStatus[];
	containerStatus: SearchContainerStatus[];
}

export type SearchIssueStatusCategory = 'OPEN' | 'DONE' | 'INDETERMINATE' | 'UNDEFINED' | 'NEW';

export interface JiraFilters {
	issueFilter: {
		projectARIs: string[];
		assigneeARIs: string[];
		watcherARIs: string[];
		issueTypeIDs: string[];
		// TODO - make reportARIs and issueLabels required when cleaning up rovo_search_in_jira_t25_fps_filters
		reporterARIs?: string[];
		issueLabels?: string[];
		issueTypes: string[];
		commenterARIs: string[];
		statusCategories?: SearchIssueStatusCategory[];
	};
	projectFilter?: {
		projectTypes?: SearchJiraProductType[];
	};
}

export interface AssetsFilters {
	objectSchemaFilter: {
		schemaARIs: string[];
	};
	objectTypeFilter: {
		typeARIs: string[];
	};
}

export interface MercuryFilters {
	owners: string[];
	focusAreaTypeIds: string[];
	ancestorIds: string[];
}

export interface TalentFilters {
	owners: string[];
	employmentTypes: string[];
	focusAreas: string[];
	jobFamilies: string[];
	statuses: string[];
	teams: string[];
}
export interface CompassFilters {
	componentFilter?: {
		componentStates: string[];
	};
}

export type SearchThirdPartyRangeField = 'LASTMODIFIED' | 'CREATED';

export interface ThirdPartyRangeFilter {
	field: SearchThirdPartyRangeField;
	gt?: string;
	lt?: string;
}

export interface SalesforceFilters {
	stage?: string[];
	closeDate?: string;
	owners?: string[];
	isClosed?: boolean;
	campaignStatuses?: string[];
	caseStatuses?: string[];
	knowledgeArticlePublishStatuses?: string[];
	eventTypes?: string[];
	leadStatuses?: string[];
	taskStatuses?: string[];
	taskTypes?: string[];
}

export interface ThirdPartyFilters {
	range?: ThirdPartyRangeFilter[];
	titleMatchOnly: boolean;
	subtypes: string[];
	integrations?: string[];
	thirdPartyProducts?: ThirdPartyProductFilter[] | undefined;
	assignees?: string[];
	containerAris?: string[];
	createdBy?: string[];
	lastUpdatedBy?: string[];
	ancestorAris?: string[];
	labels?: string[];
	additionalTexts?: string[];
	containerTypes?: string[];
	excludeSubtypes?: string[];
	salesforceFilters?: SalesforceFilters;
	gitFilters?: GitFilters;
	teamsFilters?: TeamsFilters;
}

export interface GitFilters {
	pullRequestStatus?: string[];
	repoName?: string[];
	reviewer?: string[];
}

export interface TeamsFilters {
	hasAttachment?: boolean;
}

export interface ThirdPartyProductFilter {
	integrationId: string;
	providerId?: string;
	subtypes: string[];
	product: string;
	connectorSources?: string[];
	/**
	 * Subset of `connectorSources` for which the connector is being served via impersonation
	 * (the user is NOT OAuthed AND impersonation is enabled for this product). Used by the
	 * aggregator to log success/failure of impersonated cross-product search requests so
	 * impersonation rollout can be observed. Gated behind `weaklink-logging-fg`.
	 * Passed through to aggregator via thirdPartyFilters.thirdPartyProducts; backend must support
	 * `connectorSourcesImpersonationEnabled: [String!]` on the SearchThirdPartyProduct input.
	 */
	connectorSourcesImpersonationEnabled?: string[];
	datasourceId?: string;
	connectionIds?: string[];
	entities?: string[];
	/**
	 * Entity types to return as linked entities within each result (e.g., ["ati:cloud:graph:comment"]).
	 * Used for embedded comments search where comments are returned within parent document results.
	 * Passed through to aggregator via thirdPartyFilters.thirdPartyProducts; backend must support in SearchThirdPartyProduct input.
	 */
	linkedEntities?: string[];
	/**
	 * Specifies how much content to return for linked entities.
	 * Use "ONLY_LINKED_ENTITIES" to force match only on linked entities (e.g., only documents where comments matched).
	 * Passed through to aggregator via thirdPartyFilters.thirdPartyProducts.
	 */
	linkedEntityGranularity?: string;
	/**
	 * Channel names to filter search results to. Supported for slack channels only at the moment.
	 * Passed through to aggregator via thirdPartyFilters.thirdPartyProducts; backend must support
	 * `channels: [String!]` on the SearchThirdPartyProduct input.
	 */
	channels?: string[];
}

export type SearchPageQueryVariables = {
	query: string;
	cloudIdARI: string | string[];
	tenantId: string;
	limit: number;
	entities: EntityATI[]; // ATI of the types needed
	commonFilters: CommonFilters;
	confluenceFilters: ConfluenceFilters;
	jiraFilters: JiraFilters;
	mercuryFilters: MercuryFilters;
	talentFilters?: TalentFilters;
	compassFilters: CompassFilters;
	thirdPartyFilters: ThirdPartyFilters;
	assetsFilters: AssetsFilters;
	queryVersion: number;
	searchSessionId: string;
	sessionId?: string; // Used for correlating events in BE analytics.
	sourceProduct?: string;
	sort: ResultsSort[];
	experimentId?: string;
	shadowExperimentId?: string;
	experimentLayers?: SearchExperimentLayer[];
	after?: string;
	disableQueryReplacement?: boolean;
	experience?: string;
	enableHighlighting?: boolean;
	skip3pEntityHydration?: boolean;
	includeGithubEntityHydration?: boolean;
	includeSocialSignals?: boolean;
	includeUserSignals?: boolean;
	includeJiraIssueType?: boolean;
	includeJiraBoardContainer?: boolean;
	fanoutExperimentCohort?: string;
	includeSpaceSubtype?: boolean;
	includeConfluenceEntity?: boolean;
	skipFieldsFor3pInQuickFind?: boolean;
	includeAutocompleteSuggestions?: boolean;
};
export type ResultsCountQueryVariables = {
	query: string;
	cloudIdARI: string;
	limit: number;
	entities: EntityATI[]; // ATI of the types needed
	commonFilters: CommonFilters;
	confluenceFilters: ConfluenceFilters;
	jiraFilters: JiraFilters;
	mercuryFilters: MercuryFilters;
	compassFilters: CompassFilters;
	thirdPartyFilters: ThirdPartyFilters;
	assetsFilters: AssetsFilters;
	talentFilters?: TalentFilters;
	queryVersion: number;
	searchSessionId: string;
	sourceProduct?: string;
	sort: any[];
	after?: string;
	disableQueryReplacement?: boolean;
};

export type DcSpaceFilterOptionsQueryVariables = {
	query: string;
	cloudIdARI: string;
	limit: number;
	entities: EntityATI[];
	product: string;
	integration?: string;
	containerTypes?: string[];
};

export type ThirdPartySpaceFilterOptionsQueryVariables = {
	query: string;
	cloudIdARI: string[];
	limit: number;
	entities: EntityATI[];
	product: string;
	integration: string;
	subtypes: string[];
};

export type ThirdPartyRepositoryFilterOptionsQueryVariables =
	ThirdPartySpaceFilterOptionsQueryVariables;

export type SpaceARIEntitiesQueryVariables = {
	ids: string[];
};

export type DocumentARIEntitiesQueryVariables = {
	ids: string[];
};

export type ConfluenceDcContentFilterOptionsQueryVariables = {
	query: string;
	cloudIdARI: string;
	limit: number;
	entities: EntityATI[];
	product: string;
};

export type ConfluenceDcPagesQueryVariables = {
	query: string;
	cloudIdARI: string;
	limit: number;
	entities: EntityATI[];
	product: string;
	ancestorIds: string[];
};

/**
 * These attributes are the base search result attributes and are requested for every entity.
 */
export const SEARCH_RESULTS_ATTRIBUTES = `
description
iconUrl
id
title
type
url
__typename
`;

export const SEARCH_PAGE_OPERATION_NAME = 'SearchPageQuery';
export const SEARCH_DIALOG_OPERATION_NAME = 'SearchDialogQuery';

const includeUserSignalsParam = (serveUserShardUI: boolean = false) => {
	if (serveUserShardUI) {
		return '\n		$includeUserSignals: Boolean!';
	} else {
		return '';
	}
};

export const queryGenerator = (
	operationName: string,
	serveUserShardUI: boolean = false,
): string => {
	const partials = getSupportedPartials(serveUserShardUI);

	const graphQuery = `
	query ${operationName}(
		$query: String!
		$cloudIdARI: [String!]!
		$tenantId: ID!
		$limit: Int!
		$entities: [String!]!
		$queryVersion: Int
		$searchSessionId: String
		$sessionId: String
		$commonFilters: SearchCommonFilter
		$confluenceFilters: SearchConfluenceFilter
		$jiraFilters: SearchJiraFilter
		$mercuryFilters: SearchMercuryFilter
		${fg('talent_global_search') ? '$talentFilters: SearchTalentFilter' : ''}
		$compassFilters: SearchCompassFilter
		$thirdPartyFilters:  SearchThirdPartyFilter
		${fg('assets_rovo_search_all') ? '$assetsFilters: SearchAssetsFilter' : ''}
		$sort: [SearchSortInput]
		$sourceProduct: String
		$experimentId: String
		$shadowExperimentId: String
		$experimentLayers: [SearchExperimentLayer]
		$after: String
		$disableQueryReplacement: Boolean
		$experience: String!
		$enableHighlighting: Boolean
		$skip3pEntityHydration: Boolean!
		$includeGithubEntityHydration: Boolean! = false
		$includeJiraIssueType: Boolean!
		$includeJiraBoardContainer: Boolean!
		$includeSocialSignals: Boolean!${includeUserSignalsParam(serveUserShardUI)}
		$fanoutExperimentCohort: String
		$includeSpaceSubtype: Boolean!
		$includeConfluenceEntity: Boolean!
		$skipFieldsFor3pInQuickFind: Boolean!
		$includeAutocompleteSuggestions: Boolean = false
	) {
		search {
			results: search(
				experience: $experience
				enableHighlighting: $enableHighlighting
				fanoutExperimentCohort: $fanoutExperimentCohort
				query: $query
				first: $limit
				after: $after
				sort: $sort
				disableQueryReplacement: $disableQueryReplacement
				filters: {
					entities: $entities
					locations: $cloudIdARI
					commonFilters: $commonFilters
					confluenceFilters: $confluenceFilters
					jiraFilters: $jiraFilters
					${fg('assets_rovo_search_all') ? 'assetsFilters: $assetsFilters' : ''}
					mercuryFilters: $mercuryFilters
					compassFilters: $compassFilters
					thirdPartyFilters: $thirdPartyFilters
					${fg('talent_global_search') ? 'talentFilters: $talentFilters' : ''}
				}
				analytics: {
					queryVersion: $queryVersion
					searchSessionId: $searchSessionId
					sessionId: $sessionId
					sourceProduct: $sourceProduct
				}
				experimentContext: {
					experimentId: $experimentId
					shadowExperimentId: $shadowExperimentId
					experimentLayers: $experimentLayers
				}
			) {
				edges {
					node {
						${SEARCH_RESULTS_ATTRIBUTES}
						${partials}
					}
				}
				queryInfo {
					originalQuery
					replacementQuery
					suggestedQuery
					originalQueryType
					confidenceScore
					shouldTriggerAutocorrectionExperience
				}
				abTest {
					abTestId
					controlId
					experimentId
				}
				totalCount
				totalCounts {
					product
					count
				}
        pageInfo {
          endCursor
        }
				autocompleteSuggestions @include(if: $includeAutocompleteSuggestions)
			}
		}
	}
	`;
	return graphQuery;
};

export const resultsCountOperationName = 'ResultsCountQuery';

export const resultsCountQueryGenerator = (): string => {
	const graphQuery = `
	query ${resultsCountOperationName}(
		$query: String!
		$cloudIdARI: String!
		$limit: Int!
		$entities: [String!]!
		$queryVersion: Int
		$searchSessionId: String
		$commonFilters: SearchCommonFilter
		$confluenceFilters: SearchConfluenceFilter
		$jiraFilters: SearchJiraFilter
		$mercuryFilters: SearchMercuryFilter
		${fg('talent_global_search') ? '$talentFilters: SearchTalentFilter' : ''}
		$compassFilters: SearchCompassFilter
		$thirdPartyFilters:  SearchThirdPartyFilter
		${fg('assets_rovo_search_all') ? '$assetsFilters: SearchAssetsFilter' : ''}
		$sort: [SearchSortInput]
		$sourceProduct: String
		$experimentId: String
		$shadowExperimentId: String
		$experimentLayers: [SearchExperimentLayer]
		$disableQueryReplacement: Boolean
	) {
		search {
			results: search(
				experience: "search-page"
				query: $query
				first: $limit
				sort: $sort
				disableQueryReplacement: $disableQueryReplacement
				filters: {
					entities: $entities
					locations: [$cloudIdARI]
					commonFilters: $commonFilters
					confluenceFilters: $confluenceFilters
					jiraFilters: $jiraFilters
					mercuryFilters: $mercuryFilters
					${fg('talent_global_search') ? 'talentFilters: $talentFilters' : ''}
					compassFilters: $compassFilters
					thirdPartyFilters: $thirdPartyFilters
					${fg('assets_rovo_search_all') ? 'assetsFilters: $assetsFilters' : ''}
				}
		analytics: {
					queryVersion: $queryVersion
					searchSessionId: $searchSessionId
					sourceProduct: $sourceProduct
				}
		experimentContext: {
			experimentId: $experimentId
			shadowExperimentId: $shadowExperimentId
			experimentLayers: $experimentLayers
		}
			) {

				queryInfo {
					originalQuery
					replacementQuery
					originalQueryType
					confidenceScore
					shouldTriggerAutocorrectionExperience
				}
				abTest {
					abTestId
					controlId
					experimentId
				}
				totalCount
				totalCounts {
					product
					count
				}
			}
		}
	}
	`;
	return graphQuery;
};

export const jiraDcProjectFilterOptionsOperationName = 'jiraDcProjectFilterOptionsQuery';
export const confluenceDcSpaceFilterOptionsOperationName = 'confluenceDcSpaceFilterOptionsQuery';
export const thirdPartySpaceFilterOptionsOperationName = 'thirdPartySpaceFilterOptionsQuery';
export const thirdPartySpaceFilterOptionsQueryGenerator = (): string => {
	const graphQuery = `
		query ${thirdPartySpaceFilterOptionsOperationName} (
			$query: String!
			$cloudIdARI: [String!]!
			$limit: Int!
			$entities: [String!]!
			$product: String!
			$integration: String!
			$subtypes: [String!]!
		) {
		search {
			search(
			query: $query
			first: $limit
			filters: {
				entities: $entities,
				thirdPartyFilters: {
					titleMatchOnly: true,
					thirdPartyProducts:[
						{
							product: $product,
							integrationId: $integration,
							subtypes: $subtypes
						}
					]
				},
				locations: $cloudIdARI
			}
			experience: "search-page"
			) {
				edges {
					node {
						...on SearchResultGraphDocument {
							entity {
								...on ExternalSpace {
									id
									displayName
									icon {
										url
									}
								}
							}
						}
					}
				}
			}
		}
	}
	`;
	return graphQuery;
};

export const teamsWorkspaceFilterOptionsOperationName = 'teamsWorkspaceFilterOptionsQuery';
export const teamsWorkspaceFilterOptionsQueryGenerator = (): string => {
	const graphQuery = `
		query ${teamsWorkspaceFilterOptionsOperationName} (
			$query: String!
			$cloudIdARI: [String!]!
			$limit: Int!
			$entities: [String!]!
			$product: String!
			$integration: String!
			$subtypes: [String!]!
		) {
		search {
			search(
			query: $query
			first: $limit
			filters: {
				entities: $entities,
				thirdPartyFilters: {
					titleMatchOnly: true,
					thirdPartyProducts:[
						{
							product: $product,
							integrationId: $integration,
							subtypes: $subtypes
						}
					]
				},
				locations: $cloudIdARI
			}
			experience: "search-page"
			) {
				edges {
					node {
						...on SearchResultGraphDocument {
							entity {
								...on ExternalConversation {
									workspace
									id
								}
							}
						}
					}
				}
			}
		}
	}
	`;
	return graphQuery;
};

export const thirdPartyRepositoryFilterOptionsOperationName =
	'thirdPartyRepositoryFilterOptionsQuery';
export const thirdPartyRepositoryFilterOptionsQueryGenerator = (): string => {
	const graphQuery = `
		query ${thirdPartyRepositoryFilterOptionsOperationName} (
			$query: String!
			$cloudIdARI: [String!]!
			$limit: Int!
			$entities: [String!]!
			$product: String!
			$integration: String!
			$subtypes: [String!]!
		) {
		search {
			search(
			query: $query
			first: $limit
			filters: {
				entities: $entities,
				thirdPartyFilters: {
					titleMatchOnly: true,
					thirdPartyProducts:[
						{
							product: $product,
							integrationId: $integration,
							subtypes: $subtypes
						}
					]
				},
				locations: $cloudIdARI
			}
			experience: "search-page"
			) {
				edges {
					node {
						...on SearchResultGraphDocument {
							entity {
								...on ExternalRepository {
									id
									name
								}
							}
						}
					}
				}
			}
		}
	}
	`;
	return graphQuery;
};

export const spaceARIEntitiesOperationName = 'spaceIdFilterOptionsQuery';
export const spaceARIEntitiesQueryGenerator = (): string => {
	const graphQuery = `
		query ${spaceARIEntitiesOperationName} ($ids: [ID!]!) {
			external_entities(ids: $ids) @optIn(to: "ExternalDataDepotQueryV2"){
				space {
					id
					displayName
					icon {
						url
					}
				}
			}
		}`;
	return graphQuery;
};

export const documentARIEntitiesOperationName = 'documentIdFilterOptionsQuery';
export const documentARIEntitiesQueryGenerator = (): string => {
	const graphQuery = `
		query ${documentARIEntitiesOperationName} ($ids: [ID!]!) {
			external_entities(ids: $ids) @optIn(to: "ExternalDataDepotQueryV2"){
				document {
					id
					displayName
					container @optIn(to: "ExternalDataDepotQueryV2") {
                        ... on ExternalSpace {
                            id
                        }
                    }
				}
			}
		}`;
	return graphQuery;
};

export const confluenceDcContentFilterOptionsOperationName =
	'confluenceDcContentFilterOptionsQuery';
export const confluenceDcContentFilterOptionsQueryGenerator = (): string => {
	const graphQuery = `
		query ${confluenceDcContentFilterOptionsOperationName} (
			$query: String!
			$cloudIdARI: String!
			$limit: Int!
			$entities: [String!]!
			$product: String!
		) {
		search {
			search(
			query: $query
			first: $limit
			filters: {
				entities: $entities,
				locations: [$cloudIdARI],
				thirdPartyFilters: {
					thirdPartyProducts:[
						{
							product: $product,
							integrationId:"ari:cloud:platform::integration/confluence-dc",
							subtypes:[]
						}
					]
				}
			}
			analytics: {searchSessionId: "graphql"}
			experience: "search-page"
			) {
				edges {
					node {
					id
					title
					description
					... on SearchResultGraphDocument {
							entity {
								...on ExternalDocument {
									type {
										category
									}
									parentId
									container @optIn(to: "ExternalDataDepotQueryV2") {
										...on ExternalSpace {
											id
										}
									}
								}
							}
						}
					}
				}
			}
		}
	}
	`;
	return graphQuery;
};

export const confluenceDcPagesOperationName = 'confluenceDcPagesQuery';
export const confluenceDcPagesQueryGenerator = (): string => {
	const graphQuery = `
		query ${confluenceDcPagesOperationName} (
			$ancestorIds: [ID!]!
		) {
			external_entities(ids: $ancestorIds) @optIn(to: "ExternalDataDepotQueryV2") {
				document {
					id
					displayName
				}
			}
		}
	`;
	return graphQuery;
};

export const searchLinkedSitesOperationName = 'CollabContextWorkspaceIsConnectedToWorkspaceQuery';
export type SearchLinkedSitesQueryVariables = { id: string };
export const searchLinkedSiteQueryGenerator = (): string => {
	const graphQuery = `
		query ${searchLinkedSitesOperationName}($id: ID!) {
			collabContext_workspaceIsConnectedToWorkspace(id: $id) {
				edges {
					node {
						id
					}
					cursor
				}
				pageInfo {
					endCursor
					hasNextPage
					hasPreviousPage
					startCursor
				}
			}
		}
	`;
	return graphQuery;
};
